import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner } from "../../../constants/const";
import { GetLanguageString } from "../../helper/Components";
import { GetSelectedLanguage } from "../../helper/Method";
import { ApiFinance } from "../../funds/ApiFinance";

const ManageUnitAccounts = ({ setModal, unitId, unitName }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const [accountsList, setAccountsList] = useState([]);
  const [dimensionsList, setDimensionsList] = useState([]);

  // Initial values from API
  const [initialUnitDimension, setInitialUnitDimension] = useState(null);
  const [initialUnitCashInHandAccount, setInitialUnitCashInHandAccount] =
    useState(null);
  const [initialUnitAmanatAccount, setInitialUnitAmanatAccount] =
    useState(null);

  // Selected values
  const [selectedUnitDimension, setSelectedUnitDimension] = useState(null);
  const [selectedUnitCashInHandAccount, setSelectedUnitCashInHandAccount] =
    useState(null);
  const [selectedUnitAmanatAccount, setSelectedUnitAmanatAccount] =
    useState(null);

  const [errors, setErrors] = useState({});

  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  useEffect(() => {
    setLoading(true);
    setButtonDisable(true);
    ApiFinance()
      .GetFinanceAccountDetail(unitId)
      .then((res) => {
        setAccountsList(res.data.accountsList);
        setDimensionsList(res.data.dimensionsList);

        setInitialUnitDimension(res.data.unitDimension);
        setInitialUnitCashInHandAccount(res.data.cashInHandAccount);
        setInitialUnitAmanatAccount(res.data.amanatAccount);

        setSelectedUnitDimension(res.data.unitDimension);
        setSelectedUnitCashInHandAccount(res.data.cashInHandAccount);
        setSelectedUnitAmanatAccount(res.data.amanatAccount);

        setLoading(false);
        setButtonDisable(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
        setButtonDisable(false);
      });
  }, [unitId]);

  const validate = () => {
    let temp = {};

    const noDimensionSelected =
      selectedUnitDimension === null && initialUnitDimension === null;

    if (noDimensionSelected) {
      temp.dimensionError = (
        <GetLanguageString props="finance_unit_manage_account_select_dimension_error" />
      );
    }

    setErrors(temp);

    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      unitId,
      unitDimension:
        selectedUnitDimension !== initialUnitDimension
          ? selectedUnitDimension
          : null,
      unitCashInHandAccount:
        selectedUnitCashInHandAccount !== initialUnitCashInHandAccount
          ? selectedUnitCashInHandAccount
          : null,
      unitAmanatAccount:
        selectedUnitAmanatAccount !== initialUnitAmanatAccount
          ? selectedUnitAmanatAccount
          : null,
    };

    const allNull =
      payload.unitDimension === null &&
      payload.unitCashInHandAccount === null &&
      payload.unitAmanatAccount === null;

    if (allNull) {
      toggle();
      return;
    }
    setLoading(true);
    setButtonDisable(true);
    ApiFinance()
      .UpdateUnitFinanceAccount(payload)
      .then((res) => {
        swal({
          title: t("finance_unit_account_update"),
          className: GetSelectedLanguage() === "en" ? "" : "ur",
          icon: "success",
          buttons: "OK",
        });
        toggle();
        setLoading(false);
        setButtonDisable(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
        setButtonDisable(false);
      });
  };

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen={true} className="inmodal" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            {<GetLanguageString props="finance_unit_manage_account" />}
            <br></br>
            <span className="ur">({unitName})</span>
          </ModalHeader>
          {!loading ? (
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_unit_manage_account_select_dimension" />
                    }
                  </Label>
                  <Select
                    value={selectedUnitDimension}
                    options={dimensionsList}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={(v) => {
                      setSelectedUnitDimension(v ?? null);
                      setErrors((prev) => ({ ...prev, dimensionError: "" }));
                    }}
                  />
                  {errors.dimensionError && (
                    <h4 className="text-error">{errors.dimensionError}</h4>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_unit_manage_account_select_cash_in_hand_account" />
                    }
                  </Label>
                  <Select
                    value={selectedUnitCashInHandAccount}
                    options={accountsList}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={setSelectedUnitCashInHandAccount}
                  />
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_unit_manage_account_select_amanat_account" />
                    }
                  </Label>
                  <Select
                    value={selectedUnitAmanatAccount}
                    options={accountsList}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={setSelectedUnitAmanatAccount}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color="primary" disabled={buttonDisable} type="submit">
                  {<GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default ManageUnitAccounts;
